import { computed, Directive, input } from '@angular/core';

import { buttonEnumInputTransformer, ButtonEnumInputType } from './button-common-transformers';

export enum CommonButtonFilledSize {
	XS = 'XS',
	S = 'S',
	M = 'M',
	L = 'L',
}

export enum CommonButtonFilledState {
	DEFAULT = 'DEFAULT',
	SELECTED = 'SELECTED',
	DISABLED = 'DISABLED',
}

export enum CommonButtonFilledColor {
	DEFAULT = 'DEFAULT',
	PRIMARY = 'PRIMARY',
}

const CLASSES_BY_SIZE_MAP: Record<CommonButtonFilledSize, string[]> = {
	[CommonButtonFilledSize.XS]: ['rounded-0.75', 'py-0.5', 'pl-1', 'pr-1.5'],
	[CommonButtonFilledSize.S]: ['rounded-1', 'py-0.75', 'px-1.5'],
	[CommonButtonFilledSize.M]: ['rounded-1', 'py-1', 'px-2'],
	[CommonButtonFilledSize.L]: ['rounded-1', 'py-1', 'px-3'],
};

const CLASSES_BY_COLOR_AND_STATE_MAP: Record<
	CommonButtonFilledColor,
	Record<CommonButtonFilledState, string[]>
> = {
	[CommonButtonFilledColor.DEFAULT]: {
		[CommonButtonFilledState.DEFAULT]: [
			'bg-260',
			'fc-500',
			'hover:fc-800',
		],
		[CommonButtonFilledState.SELECTED]: ['fc-800', 'bg-primary/20'],
		[CommonButtonFilledState.DISABLED]: ['bg-270', 'fc-200'],
	},
	[CommonButtonFilledColor.PRIMARY]: {
		[CommonButtonFilledState.DEFAULT]: ['bg-primary', 'fc-white', 'hover:bg-primary-700'],
		[CommonButtonFilledState.SELECTED]: ['bg-fg-800', 'text-190'],
		[CommonButtonFilledState.DISABLED]: ['bg-fg-200', 'text-190'],
	},
};

@Directive({
	selector: '[commonButtonFilled]',
	standalone: true,
	host: {
		class: 'flex items-center justify-start gap-0.75',
		'[class]': '$classes()',
		'[attr.data-size]': '$size()',
		'[attr.data-state]': '$state()',
		'[attr.data-color]': '$color()',
		'[attr.disabled]': '$disabled()',
	},
})
export class CommonButtonFilledDirective {
	public readonly $size = input<
		CommonButtonFilledSize,
		ButtonEnumInputType<CommonButtonFilledSize>
	>(CommonButtonFilledSize.M, {
		alias: 'size',
		transform: (value) =>
			buttonEnumInputTransformer(
				value,
				Object.values(CommonButtonFilledSize),
				CommonButtonFilledSize.M
			),
	});

	public readonly $color = input<
		CommonButtonFilledColor,
		ButtonEnumInputType<CommonButtonFilledColor>
	>(CommonButtonFilledColor.DEFAULT, {
		alias: 'color',
		transform: (value) =>
			buttonEnumInputTransformer(
				value,
				Object.values(CommonButtonFilledColor),
				CommonButtonFilledColor.DEFAULT
			),
	});

	public readonly $state = input<
		CommonButtonFilledState,
		ButtonEnumInputType<CommonButtonFilledState>
	>(CommonButtonFilledState.DEFAULT, {
		alias: 'state',
		transform: (value) =>
			buttonEnumInputTransformer(
				value,
				Object.values(CommonButtonFilledState),
				CommonButtonFilledState.DEFAULT
			),
	});

	protected readonly $classes = computed(() => {
		const size = this.$size();
		const state = this.$state();
		const color = this.$color();

		const classesBySize = CLASSES_BY_SIZE_MAP[size] ?? [];
		const classesByState = CLASSES_BY_COLOR_AND_STATE_MAP[color][state] ?? [];

		const classes = [...classesBySize, ...classesByState];

		return classes.join(' ');
	});

	protected readonly $disabled = computed(() => {
		const state = this.$state();
		return state === CommonButtonFilledState.DISABLED;
	});
}
